import { useContext, useEffect, useRef, useState } from 'react';

import { SignalRContext } from 'contexts/signalR';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeActiveTranscriptCallSelector,
  CallScribeProtectedTranscriptCallSelector,
} from 'store/callScribe/selectors';
import { applyFetchedParticipants } from 'store/callScribe';
import { getCallScribeParticipants } from 'services/callScribe';

type Dispatch = (action: ReturnType<typeof applyFetchedParticipants>) => unknown;

export const fetchAndStoreParticipants = async (
  dispatch: Dispatch,
  callId: number,
): Promise<void> => {
  const requestedAt = Date.now();
  const rows = await Promise.resolve()
    .then(() => getCallScribeParticipants(callId))
    .then(res => res?.data, () => undefined);
  if (Array.isArray(rows)) {
    dispatch(applyFetchedParticipants({
      callId,
      participants: rows,
      requestedAt,
    }));
  }
};

export const useParticipantsBackfill = (): void => {
  const dispatch = useAppDispatch();
  const activeId = useAppSelector(CallScribeActiveTranscriptCallSelector);
  const protectedId = useAppSelector(CallScribeProtectedTranscriptCallSelector);
  const activeListed = useAppSelector(state => (
    typeof activeId === 'number' && Boolean(state.callScribe.participantsByCallId[activeId])
  ));
  const protectedListed = useAppSelector(state => (
    typeof protectedId === 'number' && Boolean(state.callScribe.participantsByCallId[protectedId])
  ));
  const { connection } = useContext(SignalRContext);
  const [generation, setGeneration] = useState(0);
  const fetchedRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!connection) return undefined;
    const bump = (): void => { setGeneration(n => n + 1); };
    connection.onreconnected(bump);
    return undefined;
  }, [connection]);

  useEffect(() => {
    const listed = new Map<number | undefined, boolean>([
      [activeId, activeListed],
      [protectedId, protectedListed],
    ]);
    listed.forEach((hasList, callId) => {
      if (typeof callId !== 'number' || inFlightRef.current.has(callId)) return;
      const key = `${callId}|${generation}`;
      if (hasList && fetchedRef.current.has(key)) return;
      fetchedRef.current.add(key);
      inFlightRef.current.add(callId);
      void fetchAndStoreParticipants(dispatch as unknown as Dispatch, callId).finally(() => {
        inFlightRef.current.delete(callId);
      });
    });
  }, [activeId, protectedId, activeListed, protectedListed, generation, dispatch]);
};
